/**
 * Admin → Marketing → SMS Health
 * ──────────────────────────────────────────────────────────────────────────
 * Route: /admin/marketing/sms-health
 * Roles: super_admin, manager, marketing (gated server-side too)
 *
 * Sections:
 *  1. Config card — live MSG91 sendsms DLT config snapshot
 *  2. KPI strip — last 24h / 7d send counts (success vs failed)
 *  3. Send Test SMS — fires a real OTP via the approved DLT template
 *  4. Recent Sends table — latest rows from `sms_messages`
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminShell from '../../../components/admin/AdminShell';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Skeleton } from '../../../components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/ui/dialog';
import {
  MessageSquare, RefreshCw, Send, CheckCircle2, XCircle, AlertTriangle,
  Phone, ShieldCheck, ShieldAlert, Copy, Loader2,
} from 'lucide-react';

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: BASE_URL });
api.interceptors.request.use((c) => {
  const t =
    localStorage.getItem('su_token') ||
    localStorage.getItem('admin_token') ||
    localStorage.getItem('token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      timeZone: 'Asia/Kolkata',
    });
  } catch {
    return iso;
  }
}

function maskPhone(p) {
  if (!p) return '—';
  if (p.length > 8) return `${p.slice(0, 6)}****${p.slice(-4)}`;
  return '****';
}

function StatusBadge({ status }) {
  if (status === 'success') {
    return (
      <Badge
        data-testid="sms-status-badge-success"
        className="bg-[rgba(61,220,151,0.12)] border-[rgba(61,220,151,0.35)] text-[#BFF3D2] gap-1.5"
      >
        <CheckCircle2 size={12} /> Success
      </Badge>
    );
  }
  return (
    <Badge
      data-testid="sms-status-badge-failed"
      className="bg-[rgba(255,77,77,0.12)] border-[rgba(255,77,77,0.35)] text-[#FFD7D7] gap-1.5"
    >
      <XCircle size={12} /> Failed
    </Badge>
  );
}

function KpiTile({ label, value, hint, accent, testId }) {
  const accentMap = {
    neutral: 'text-[#F2F2F2]',
    good: 'text-[#BFF3D2]',
    bad: 'text-[#FFD7D7]',
    gold: 'text-[#F1E2B8]',
  };
  return (
    <Card
      className="surface-card card-highlight rounded-xl border-[#2A2A2A] bg-[#0E0E0E] p-4"
      data-testid={testId}
    >
      <p className="text-[11px] uppercase tracking-wider text-[#8A8A8A] font-medium">{label}</p>
      <p className={`mt-1.5 font-['EB_Garamond'] text-3xl ${accentMap[accent] || accentMap.neutral}`}>{value}</p>
      {hint && <p className="mt-1 text-xs text-[#B9B9B9]">{hint}</p>}
    </Card>
  );
}

function ConfigRow({ label, value, mono = false, ok = null }) {
  let pillIcon = null;
  if (ok === true) pillIcon = <ShieldCheck size={14} className="text-[#3DDC97]" />;
  if (ok === false) pillIcon = <ShieldAlert size={14} className="text-[#FF6B6B]" />;
  return (
    <div className="flex items-center justify-between gap-4 py-2 border-b border-[#1E1E1E] last:border-b-0">
      <span className="text-xs uppercase tracking-wider text-[#8A8A8A]">{label}</span>
      <span className={`text-sm ${mono ? 'font-mono' : ''} text-[#F2F2F2] flex items-center gap-2 break-all text-right`}>
        {pillIcon}
        {value || <em className="text-[#6F6F6F]">not set</em>}
      </span>
    </div>
  );
}

export default function SmsHealthPage() {
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSource, setFilterSource] = useState('all');

  // Test send form state
  const [testPhone, setTestPhone] = useState('');
  const [sending, setSending] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testDialogOpen, setTestDialogOpen] = useState(false);

  const loadAll = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    else setRefreshing(true);
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterSource !== 'all') params.source = filterSource;

      const [cfgRes, msgRes] = await Promise.all([
        api.get('/api/admin/sms/config'),
        api.get('/api/admin/sms/messages', { params: { limit: 100, ...params } }),
      ]);
      setConfig(cfgRes.data?.config || null);
      setMessages(msgRes.data?.messages || []);
      setStats(msgRes.data?.stats || null);
    } catch (err) {
      console.error('[SmsHealth] load failed', err);
      toast.error(err?.response?.data?.detail || 'Failed to load SMS health data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filterStatus, filterSource]);

  useEffect(() => { loadAll(true); }, [loadAll]);

  const handleSendTest = async () => {
    const phone = testPhone.trim();
    if (!phone) {
      toast.error('Enter a phone number first');
      return;
    }
    setSending(true);
    setTestResult(null);
    try {
      const res = await api.post('/api/admin/sms/test', { phone });
      setTestResult(res.data);
      setTestDialogOpen(true);
      if (res.data?.success) {
        toast.success(`OTP sent to ${maskPhone(res.data.phone_e164 || phone)}`);
      } else {
        toast.error(`Send failed — ${res.data?.message || 'unknown error'}`);
      }
      // Refresh list so the new row appears
      loadAll(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`Send error — ${detail}`);
      setTestResult({ success: false, message: detail });
      setTestDialogOpen(true);
    } finally {
      setSending(false);
    }
  };

  const copyJson = (obj) => {
    try {
      navigator.clipboard.writeText(JSON.stringify(obj, null, 2));
      toast.success('Copied JSON to clipboard');
    } catch {
      toast.error('Copy failed');
    }
  };

  const cfgComplete =
    config && config.auth_key_set && config.dlt_entity_id && config.dlt_template_id;

  return (
    <AdminShell
      title="SMS Health"
      actions={
        <Button
          variant="outline"
          size="sm"
          onClick={() => loadAll(false)}
          disabled={refreshing}
          className="border-[#2A2A2A] bg-transparent text-[#B9B9B9] hover:border-[#D4A843]/40 gap-2"
          data-testid="sms-health-refresh-button"
        >
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
          Refresh
        </Button>
      }
    >
      <div className="space-y-6">
        {/* Overall health banner */}
        {!loading && (
          <div
            className={`rounded-xl border px-4 py-3 flex items-center gap-3 ${
              cfgComplete
                ? 'border-[rgba(61,220,151,0.35)] bg-[rgba(61,220,151,0.06)]'
                : 'border-[rgba(255,77,77,0.35)] bg-[rgba(255,77,77,0.06)]'
            }`}
            data-testid="sms-health-banner"
          >
            {cfgComplete ? (
              <ShieldCheck className="text-[#3DDC97]" size={20} />
            ) : (
              <ShieldAlert className="text-[#FF6B6B]" size={20} />
            )}
            <div className="flex-1">
              <p className="text-sm font-medium text-[#F2F2F2]">
                {cfgComplete
                  ? 'MSG91 DLT configuration looks healthy.'
                  : 'MSG91 DLT configuration is incomplete — OTP sends will fail.'}
              </p>
              <p className="text-xs text-[#B9B9B9] mt-0.5">
                Sender <span className="font-mono text-[#F1E2B8]">{config?.sender_id || '—'}</span> · PE ID{' '}
                <span className="font-mono text-[#F1E2B8]">{config?.dlt_entity_id || '—'}</span> · Template{' '}
                <span className="font-mono text-[#F1E2B8]">{config?.dlt_template_id || '—'}</span>
              </p>
            </div>
          </div>
        )}

        {/* KPI Strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="surface-card border-[#2A2A2A] bg-[#0E0E0E] p-4">
                <Skeleton className="h-3 w-24 mb-2" />
                <Skeleton className="h-8 w-16" />
              </Card>
            ))
          ) : (
            <>
              <KpiTile
                label="Sends · 24h"
                value={stats?.total_24h ?? 0}
                hint={`${stats?.success_24h ?? 0} ok · ${stats?.failed_24h ?? 0} failed`}
                accent="gold"
                testId="sms-kpi-total-24h"
              />
              <KpiTile
                label="Success · 24h"
                value={stats?.success_24h ?? 0}
                hint={
                  stats?.total_24h
                    ? `${Math.round(((stats.success_24h ?? 0) / stats.total_24h) * 100)}% delivery rate`
                    : 'no traffic'
                }
                accent="good"
                testId="sms-kpi-success-24h"
              />
              <KpiTile
                label="Failed · 24h"
                value={stats?.failed_24h ?? 0}
                hint={
                  stats?.total_24h
                    ? `${Math.round(((stats.failed_24h ?? 0) / stats.total_24h) * 100)}% failure rate`
                    : 'no traffic'
                }
                accent={stats?.failed_24h > 0 ? 'bad' : 'neutral'}
                testId="sms-kpi-failed-24h"
              />
              <KpiTile
                label="Sends · 7d"
                value={stats?.total_7d ?? 0}
                hint={`${stats?.success_7d ?? 0} ok · ${stats?.failed_7d ?? 0} failed`}
                accent="neutral"
                testId="sms-kpi-total-7d"
              />
            </>
          )}
        </div>

        {/* Config + Test Send grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card
            className="surface-card card-highlight border-[#2A2A2A] bg-[#0E0E0E] p-5 lg:col-span-2"
            data-testid="sms-health-config-card"
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-['EB_Garamond'] text-lg text-[#F2F2F2]">DLT Configuration</h2>
              <span className="text-xs text-[#6F6F6F]">Reads live from backend env</span>
            </div>
            {loading || !config ? (
              <div className="space-y-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : (
              <div>
                <ConfigRow label="Sender ID" value={config.sender_id} mono />
                <ConfigRow label="PE Entity ID" value={config.dlt_entity_id} mono />
                <ConfigRow label="DLT Template ID" value={config.dlt_template_id} mono />
                <ConfigRow label="MSG91 Endpoint" value={config.sendsms_url} mono />
                <ConfigRow
                  label="Auth Key"
                  value={config.auth_key_set ? 'Loaded' : 'MISSING'}
                  ok={!!config.auth_key_set}
                />
                <ConfigRow
                  label="DB Logging"
                  value={config.db_logging ? 'Active (sms_messages)' : 'Disabled'}
                  ok={!!config.db_logging}
                />
                <div className="mt-3 pt-3 border-t border-[#1E1E1E]">
                  <p className="text-[11px] uppercase tracking-wider text-[#8A8A8A] mb-2">
                    Template body (with {'{OTP}'} placeholder)
                  </p>
                  <p
                    className="text-sm text-[#F1E2B8] bg-[#161616] border border-[#1E1E1E] rounded-md p-3 leading-relaxed font-mono"
                    data-testid="sms-health-template-body"
                  >
                    {config.template_text}
                  </p>
                </div>
              </div>
            )}
          </Card>

          {/* Send Test SMS */}
          <Card
            className="surface-card card-highlight border-[#2A2A2A] bg-[#0E0E0E] p-5 flex flex-col"
            data-testid="sms-health-test-card"
          >
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare size={18} className="text-[#D4A843]" />
              <h2 className="font-['EB_Garamond'] text-lg text-[#F2F2F2]">Send Test OTP</h2>
            </div>
            <p className="text-xs text-[#B9B9B9] mb-4 leading-relaxed">
              Fires a real DLT-compliant SMS via the approved template. The OTP is generated server-side
              and the send is recorded in <span className="font-mono text-[#F1E2B8]">sms_messages</span>{' '}
              with <span className="font-mono text-[#F1E2B8]">source=admin_test</span>.
            </p>
            <label className="text-[11px] uppercase tracking-wider text-[#8A8A8A] mb-1.5">
              Phone (10-digit IN or +91…)
            </label>
            <Input
              type="tel"
              inputMode="numeric"
              placeholder="9876543210"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              className="bg-[#161616] border-[#2A2A2A] text-[#F2F2F2] placeholder:text-[#6F6F6F] focus-visible:border-[#D4A843]/60"
              data-testid="sms-health-test-phone-input"
            />
            <Button
              onClick={handleSendTest}
              disabled={sending || !testPhone.trim() || !cfgComplete}
              className="mt-3 bg-[#D4A843] text-black hover:brightness-110 disabled:opacity-40 gap-2"
              data-testid="sms-health-send-test-button"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {sending ? 'Sending…' : 'Send Test OTP'}
            </Button>
            {!cfgComplete && (
              <p className="mt-2 text-xs text-[#FFD7D7] flex items-center gap-1.5">
                <AlertTriangle size={12} /> Fix DLT config before sending
              </p>
            )}
          </Card>
        </div>

        {/* Recent Sends */}
        <Card
          className="surface-card card-highlight border-[#2A2A2A] bg-[#0E0E0E] overflow-hidden"
          data-testid="sms-health-recent-card"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#2A2A2A] gap-3 flex-wrap">
            <h2 className="font-['EB_Garamond'] text-lg text-[#F2F2F2]">Recent Sends</h2>
            <div className="flex items-center gap-2">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger
                  className="w-[140px] bg-[#161616] border-[#2A2A2A] text-[#F2F2F2] text-xs h-9"
                  data-testid="sms-filter-status"
                >
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-[#0E0E0E] border-[#2A2A2A] text-[#F2F2F2]">
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterSource} onValueChange={setFilterSource}>
                <SelectTrigger
                  className="w-[160px] bg-[#161616] border-[#2A2A2A] text-[#F2F2F2] text-xs h-9"
                  data-testid="sms-filter-source"
                >
                  <SelectValue placeholder="Source" />
                </SelectTrigger>
                <SelectContent className="bg-[#0E0E0E] border-[#2A2A2A] text-[#F2F2F2]">
                  <SelectItem value="all">All sources</SelectItem>
                  <SelectItem value="otp">OTP</SelectItem>
                  <SelectItem value="admin_test">Admin test</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#8A8A8A] bg-[#0A0A0A]">
                  <th className="px-5 py-3 font-medium">When (IST)</th>
                  <th className="px-3 py-3 font-medium">Phone</th>
                  <th className="px-3 py-3 font-medium">Status</th>
                  <th className="px-3 py-3 font-medium">Source</th>
                  <th className="px-3 py-3 font-medium">Request ID</th>
                  <th className="px-3 py-3 font-medium">HTTP</th>
                  <th className="px-5 py-3 font-medium">Error</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i} className="border-t border-[#1A1A1A]">
                      <td colSpan={7} className="px-5 py-3">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    </tr>
                  ))
                ) : messages.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-16 text-center">
                      <Phone size={36} className="mx-auto mb-3 text-[#6F6F6F]" />
                      <p className="text-sm text-[#B9B9B9]">No SMS sends recorded yet.</p>
                      <p className="text-xs text-[#6F6F6F] mt-1">
                        Try the &ldquo;Send Test OTP&rdquo; above to verify delivery.
                      </p>
                    </td>
                  </tr>
                ) : (
                  messages.map((m) => (
                    <tr
                      key={m.id}
                      className="border-t border-[#1A1A1A] hover:bg-[#141414] transition-colors"
                      data-testid="sms-message-row"
                    >
                      <td className="px-5 py-3 text-[#B9B9B9] whitespace-nowrap">{fmtDateTime(m.created_at)}</td>
                      <td className="px-3 py-3 text-[#F2F2F2] font-mono whitespace-nowrap">
                        {maskPhone(m.phone_e164 || m.phone_masked)}
                      </td>
                      <td className="px-3 py-3"><StatusBadge status={m.status} /></td>
                      <td className="px-3 py-3 text-[#B9B9B9] whitespace-nowrap">{m.source || '—'}</td>
                      <td className="px-3 py-3 text-[#B9B9B9] font-mono text-xs">{m.request_id || '—'}</td>
                      <td className="px-3 py-3 text-[#B9B9B9] font-mono text-xs">{m.http_status ?? '—'}</td>
                      <td className="px-5 py-3 text-[#FFD7D7] max-w-[280px] truncate" title={m.error_message || ''}>
                        {m.error_message || '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Test result dialog */}
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent
          className="bg-[#0E0E0E] border-[#2A2A2A] text-[#F2F2F2] max-w-2xl"
          data-testid="sms-test-result-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-['EB_Garamond'] text-2xl flex items-center gap-2">
              {testResult?.success ? (
                <>
                  <CheckCircle2 className="text-[#3DDC97]" /> Send Successful
                </>
              ) : (
                <>
                  <XCircle className="text-[#FF6B6B]" /> Send Failed
                </>
              )}
            </DialogTitle>
            <DialogDescription className="text-[#B9B9B9]">
              {testResult?.message || 'No message returned.'}
            </DialogDescription>
          </DialogHeader>
          {testResult && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-[#8A8A8A]">Phone</p>
                  <p className="font-mono text-[#F2F2F2]">{testResult.phone_e164 || '—'}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-[#8A8A8A]">OTP sent</p>
                  <p className="font-mono text-[#F1E2B8] text-base">{testResult.otp_sent || '—'}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-[#8A8A8A]">Request ID</p>
                  <p className="font-mono text-[#F2F2F2] text-xs break-all">{testResult.request_id || '—'}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-[#8A8A8A]">HTTP Status</p>
                  <p className="font-mono text-[#F2F2F2]">{testResult.http_status ?? '—'}</p>
                </div>
              </div>
              {testResult.message_body && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-[#8A8A8A] mb-1">Message body</p>
                  <p className="bg-[#161616] border border-[#1E1E1E] rounded-md p-3 text-[#F1E2B8] font-mono text-xs">
                    {testResult.message_body}
                  </p>
                </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[11px] uppercase tracking-wider text-[#8A8A8A]">Provider response</p>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyJson(testResult.provider_response)}
                    className="h-7 text-xs text-[#B9B9B9] hover:text-[#F2F2F2] gap-1"
                    data-testid="sms-test-copy-json-button"
                  >
                    <Copy size={12} /> Copy JSON
                  </Button>
                </div>
                <pre className="bg-[#161616] border border-[#1E1E1E] rounded-md p-3 text-xs text-[#B9B9B9] max-h-48 overflow-auto">
                  {JSON.stringify(testResult.provider_response || {}, null, 2)}
                </pre>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={() => setTestDialogOpen(false)}
              className="bg-[#D4A843] text-black hover:brightness-110"
              data-testid="sms-test-result-close-button"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}
